import { createContext, useContext, type ReactNode } from "react";

// Shape of the jersey selections action creators. Each is the result of calling
// the slice's `createSlice({...}).actions` — i.e., they return a Redux action
// object that callers will pass to dispatch().
//
// The shared jersey components (ButtonGroup, RoundedButtons, GradingComment)
// use the same redux state shape (`state.selections.selections`, `state.fields.fields`)
// across every jersey app, but the per-app slice modules are not yet
// consolidated; this context lets the shared components read the action
// creators from whichever per-app slice the host provides at render time.
export interface SelectionsActions {
  setSelection: (payload: { field: string; value: string }) => unknown;
  removeSelection: (payload: { field: string; value: string }) => unknown;
  setSingleSelection: (payload: {
    field: string;
    value: string | number | boolean | null | undefined | string[];
  }) => unknown;
}

const SelectionsActionsContext = createContext<SelectionsActions | null>(null);

export function SelectionsActionsProvider({
  value,
  children,
}: {
  value: SelectionsActions;
  children: ReactNode;
}) {
  return (
    <SelectionsActionsContext.Provider value={value}>
      {children}
    </SelectionsActionsContext.Provider>
  );
}

export function useSelectionsActions(): SelectionsActions {
  const ctx = useContext(SelectionsActionsContext);
  if (!ctx) {
    throw new Error(
      "useSelectionsActions must be used inside <SelectionsActionsProvider>"
    );
  }
  return ctx;
}
